import { useTranslate } from 'module/hooks/use-translation';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { defaultGroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import {
  faSave,
  faUsers,
  faChevronDown,
  faChevronUp,
  faEllipsisV,
} from '@fortawesome/pro-solid-svg-icons';
import { Dropdown } from 'antd';
import { SignalType } from 'api/signal/types';
import { useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { ManageViewDrawer } from './manage-view-drawer';
import {
  SidebarMenuSection,
  SectionHeader,
  SectionSubItem,
  SectionSubItemLabel,
  StyledIcon,
  FlexSpacer,
  EllipsisIcon,
  EllipsisAction,
} from './styles';

interface View {
  viewName: string;
  filter: GroupedFilter;
  signalType?: SignalType;
  myAccountSignalType?: SignalType;
  selectedSignals?: string[];
  self?: boolean;
}

export interface ViewSectionHandle {
  openViewModal: () => void;
}

interface ViewSectionProps {
  views: View[];
  selectedView: string;
  onViewSelect: (view: View) => void;
  type: 'saved' | 'shared';
  handleUpdateView?: (view: View) => Promise<void>;
  onUpdateView?: (view: View) => Promise<void>;
}

export const ViewSection = forwardRef<ViewSectionHandle, ViewSectionProps>(
  (props, ref) => {
    const {
      views,
      selectedView,
      onViewSelect,
      type,
      handleUpdateView,
      onUpdateView,
    } = props as any;
    const [isOpen, setIsOpen] = useState(true);
    const [popoverVisibleKey, setPopoverVisibleKey] = useState<string | null>(
      null,
    );
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [editingView, setEditingView] = useState<View | null>(null);
    const isSaved = type === 'saved';
    const icon = isSaved ? faSave : faUsers;
    const title = isSaved ? 'Saved Views' : 'Shared Views';

    useImperativeHandle(ref, () => ({
      openViewModal: () => {},
    }));

    const translate = useTranslate();

    const handleViewClick = useCallback(
      (view: View) => {
        onViewSelect(view);
      },
      [onViewSelect],
    );

    const handleEllipsisClick = (e: React.MouseEvent, view: View) => {
      e.stopPropagation();
      setPopoverVisibleKey(view.viewName);
    };

    const handleEdit = (view: View) => {
      setEditingView(view);
      setIsDrawerVisible(true);
      setPopoverVisibleKey(null);
    };

    const handleDrawerClose = () => {
      setIsDrawerVisible(false);
      setEditingView(() => null);
    };

    const handleDrawerSave = async (updatedView: View) => {
      if (handleUpdateView) {
        await handleUpdateView(updatedView);
      }
      handleDrawerClose();
    };

    const menuItems = (view: View) => [
      {
        key: 'edit',
        label: translate('edit'),
        icon: <FontAwesomeIcon icon={faEdit} size="sm" />,
        onClick: () => handleEdit(view),
      },
    ];

    return (
      <SidebarMenuSection>
        <SectionHeader onClick={() => setIsOpen((open) => !open)}>
          <StyledIcon icon={icon} size="sm" />
          {title}
          <FlexSpacer />
          <StyledIcon icon={isOpen ? faChevronUp : faChevronDown} size="sm" />
        </SectionHeader>
        <div>
          {isOpen &&
            views?.map((view) => (
              <SectionSubItem
                key={(view as any)?.viewId}
                selected={selectedView === view?.viewName}
                onClick={() => handleViewClick(view)}
              >
                <SectionSubItemLabel>{view?.viewName}</SectionSubItemLabel>
                <EllipsisAction>
                  <Dropdown
                    menu={{ items: menuItems(view) }}
                    trigger={['click']}
                    open={popoverVisibleKey === view.viewName}
                    onOpenChange={(visible) => {
                      setPopoverVisibleKey(visible ? view.viewName : null);
                    }}
                  >
                    <EllipsisIcon
                      icon={faEllipsisV}
                      onClick={(e) => handleEllipsisClick(e, view)}
                    />
                  </Dropdown>
                </EllipsisAction>
              </SectionSubItem>
            ))}
        </div>
        <ManageViewDrawer
          isDrawerVisible={isDrawerVisible}
          onClose={handleDrawerClose}
          editingView={editingView}
          onSave={handleDrawerSave}
          onUpdateView={onUpdateView}
          filter={
            editingView?.filter || defaultGroupedFilter(FilterGroupType.Company)
          }
        />
      </SidebarMenuSection>
    );
  },
);

ViewSection.displayName = 'ViewSection';

export const SavedViews = forwardRef<
  ViewSectionHandle,
  Omit<ViewSectionProps, 'type'>
>((props, ref) => <ViewSection {...props} type="saved" ref={ref} />);

export const SharedViews = forwardRef<
  ViewSectionHandle,
  Omit<ViewSectionProps, 'type'>
>((props, ref) => <ViewSection {...props} type="shared" ref={ref} />);

SavedViews.displayName = 'SavedViews';
SharedViews.displayName = 'SharedViews';
